import { useEffect, useState, type ReactNode } from "react";
import { AntecipadosAlerta } from "../components/AntecipadosAlerta";
import { apiPcf, apiProducao, apiPedidos, type FilaLinha, type PcfResumo, type PedidoLinha } from "../lib/api";
import { useTheme } from "../hooks/useTheme";
import { Logo } from "../components/Logo";
import { PendenciasBadge } from "../components/PendenciasBadge";
import { logout, type Usuario } from "../lib/auth";
import { MsgModal } from "../components/MsgModal";
import { confirmar } from "../lib/confirmar";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";

// Produção (menu COMPRAS-ESTOQUE-PRODUCAO › PRODUCAO):
//   FilaPcf ............... FILA_PCF (fila das PCFs ABERTAS + finalizar em lote com motivo)
//   FinalizacaoManual ..... finaliza_pcf_manual
//   ReverterCancelamento .. reverte_cancelamento_pcf

const nf = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2 = (n: number) => nf.format(n || 0);
// padrão BR (Ricardo 04/11): quantidade também com 2 casas na exibição
const nf4 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt4 = (n: number) => nf4.format(n || 0);

export function Cabecalho({ usuario, titulo, sub, onVoltar }: { usuario: Usuario; titulo: string; sub: string; onVoltar: () => void }) {
  const { theme, toggle } = useTheme();
  return (
    <header className="app-header">
      <div className="brand">
        <Logo />
        <h1>
          {titulo}
          <small>{sub}</small>
        </h1>
      </div>
      <span className="spacer" />
      <PendenciasBadge />
      <div className="user-badge" title={`Usuário: ${usuario.nomusu}`}>
        <span className="user-avatar">{usuario.apelid.charAt(0).toUpperCase()}</span>
        <span className="user-nome">{usuario.nomusu.split(" ")[0]}</span>
      </div>
      <button className="btn-secundario btn-voltar-painel" onClick={onVoltar}>
        ← Painel
      </button>
      <button className="btn-secundario" onClick={toggle} title={theme === "light" ? "Modo escuro" : "Modo claro"}>{theme === "light" ? "Modo escuro" : "Modo claro"}</button>
      <button className="btn-secundario" onClick={logout} title="Sair">Sair</button>
    </header>
  );
}

// Modal de confirmação/motivo (TELA-2 dos dois programas) — tamanho padrão dos modais do painel.
function ModalMotivo({
  titulo,
  pergunta,
  exigeMotivo,
  onConfirmar,
  onCancelar,
}: {
  titulo: string;
  pergunta: string;
  exigeMotivo: boolean;
  onConfirmar: (motivo: string) => void;
  onCancelar: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  return (
    <div className="pcf-modal-bg" onClick={onCancelar}>
      <div className="pcf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pcf-modal-topo">
          <b>{titulo}</b>
          <button className="btn-secundario" onClick={onCancelar}>Fechar</button>
        </div>
        <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
        <div className="pcf-grid">
          <label className="pcf-campo" style={{ flex: "1 1 100%" }}>
            <span>Motivo</span>
            <textarea rows={4} maxLength={500} value={motivo} autoFocus onChange={(e) => setMotivo(e.target.value)} />
          </label>
        </div>
        <p className="pcf-hint">{pergunta}</p>
        <div className="pcf-acoes">
          <button
            className="btn-acao"
            onClick={() => {
              if (exigeMotivo && !motivo.trim()) return setErro("Motivo não declarado!");
              onConfirmar(motivo);
            }}
          >
            Gravar
          </button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onCancelar}>
            Voltar
          </button>
        </div>
      </div>
    </div>
  );
}

// ================================================================ FILA_PCF
export function FilaPcf({ usuario, onVoltar }: { usuario: Usuario; onVoltar: () => void }) {
  const [linhas, setLinhas] = useState<FilaLinha[]>([]);
  const [sel, setSel] = useState<number | null>(null); // numpcf selecionada
  const [modoSelecao, setModoSelecao] = useState(false); // botao-incluir-selecao
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
  const [pedirMotivo, setPedirMotivo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [erpUrl, setErpUrl] = useState("");
  const [edPos, setEdPos] = useState<Record<number, string>>({});
  const ord = useOrdenacao(linhas, {
    pcf: (l) => l.numpcf, pro: (l) => l.nompro, peso: (l) => l.qtdpec, ent: (l) => l.datent,
    ped: (l) => l.numped || null, obs: (l) => l.observ, pos: (l) => l.posicao,
  });

  async function montar(recompactar = false) {
    try {
      setErro(null);
      const r = await apiProducao.fila(recompactar);
      if (r.length > 2000) setErro("Quantidade de Itens é superior ao máximo permitido.");
      setLinhas(r.slice(0, 2000));
      setEdPos({});
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  useEffect(() => {
    void montar(true); // Opened: recompacta as posições
    apiPcf.periodo().then((p) => setErpUrl(p.erpUrl ?? "")).catch(() => setErpUrl(""));
  }, []);

  async function mover(numpcf: number, direcao: "subir" | "descer") {
    const r = await apiProducao.filaMover(numpcf, direcao);
    if (!r.ok) return setErro(r.mensagem);
    await montar();
  }

  async function gravarPosicao(l: FilaLinha) {
    const v = Number(edPos[l.numpcf] ?? l.posicao) || 0;
    if (v === l.posicao) return;
    const r = await apiProducao.filaPosicao(l.numpcf, v);
    if (!r.ok) return setErro(r.mensagem);
    setSel(l.numpcf);
    await montar();
  }

  async function gravarData(l: FilaLinha, d: string) {
    if (!d) return;
    const r = await apiProducao.filaDatent(l.numpcf, d);
    if (!r.ok) return setErro(r.mensagem);
    await montar();
  }

  function alternarModo() {
    setMarcadas(new Set());
    setModoSelecao((m) => !m);
    setSel(null);
  }

  async function finalizar(motivo: string) {
    setPedirMotivo(false);
    if (!(await confirmar("Confirma a finalização das PCF's?"))) return;
    const r = await apiProducao.filaFinalizar([...marcadas], motivo);
    if (!r.ok) return setErro(r.mensagem);
    setOk(`${marcadas.size} PCF(s) finalizada(s).`);
    setMarcadas(new Set());
    setModoSelecao(false);
    await montar();
  }

  return (
    <div className="app">
      <Cabecalho usuario={usuario} titulo="Fila de Produção" sub="PCFs abertas por ordem de produção" onVoltar={onVoltar} />
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <MsgModal texto={ok} tipo="ok" onClose={() => setOk(null)} />
      <AntecipadosAlerta />
      <div className="plan-acoes">
        <button className="btn-secundario" onClick={() => void montar()}>
          ⟳ Atualizar
        </button>
        <button className={modoSelecao ? "btn-acao" : "btn-secundario"} onClick={alternarModo}>
          {modoSelecao ? "☑ Seleção ativa" : "☐ Selecionar para finalizar"}
        </button>
        {modoSelecao && (
          <button className="btn-acao plan-del" disabled={!marcadas.size} onClick={() => setPedirMotivo(true)}>
            🏁 Finalizar ({marcadas.size})
          </button>
        )}
      </div>
      <div className="tabela-wrap">
        <table className="tabela-pcfs">
          <thead>
            <tr>
              {modoSelecao && <th style={{ width: 32 }}></th>}
              <ThOrdena ord={ord} col="pcf" className="num">Nº PCF</ThOrdena>
              <ThOrdena ord={ord} col="pro">Produto</ThOrdena>
              {/* FILA_PCF MONTAR-TABELA (TELA-1.prc L475/L490/L512): a coluna "Peso" (Z.ZZZ.ZZ9,9999)
                  recebe cadpcf.qtdpec — mantido igual ao legado. */}
              <ThOrdena ord={ord} col="peso" className="num">Peso</ThOrdena>
              <ThOrdena ord={ord} col="ent">Data Entrega</ThOrdena>
              <ThOrdena ord={ord} col="ped" className="num">Num. Pedido</ThOrdena>
              <ThOrdena ord={ord} col="obs">Observação</ThOrdena>
              <ThOrdena ord={ord} col="pos" className="num">Posição</ThOrdena>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {linhas.length === 0 && (
              <tr>
                <td colSpan={9} className="plan-vazio">
                  Nenhuma PCF aberta.
                </td>
              </tr>
            )}
            {ord.linhas.map((l) => { const i = ord.idx(l); /* Subir/Descer = posição real na fila */ return (
              <tr
                key={l.numpcf}
                className={
                  "row-clickable" + (sel === l.numpcf ? " plan-sel" : "") + (marcadas.has(l.numpcf) ? " fila-marcada" : "")
                }
                onClick={() => {
                  if (!modoSelecao) return setSel(sel === l.numpcf ? null : l.numpcf);
                  setMarcadas((atual) => {
                    const m = new Set(atual);
                    if (m.has(l.numpcf)) m.delete(l.numpcf);
                    else m.add(l.numpcf);
                    return m;
                  });
                }}
              >
                {modoSelecao && (
                  <td className="c">
                    <input type="checkbox" readOnly checked={marcadas.has(l.numpcf)} />
                  </td>
                )}
                <td className="num">{l.numpcf}</td>
                <td>{l.nompro}</td>
                <td className="num">{fmt4(l.qtdpec)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="date" defaultValue={l.datent ?? ""} onBlur={(e) => void gravarData(l, e.target.value)} />
                </td>
                <td className="num" onClick={(e) => e.stopPropagation()}>
                  {erpUrl && l.numped ? (
                    <a href={`${erpUrl}/?go=ATCADPED&numped=${l.numped}`} target="_blank" rel="noopener">
                      {l.numped}
                    </a>
                  ) : (
                    l.numped || ""
                  )}
                </td>
                <td>{l.observ}</td>
                <td className="num" onClick={(e) => e.stopPropagation()}>
                  <input
                    className="num fila-pos"
                    inputMode="numeric"
                    value={edPos[l.numpcf] ?? String(l.posicao)}
                    onChange={(e) => setEdPos({ ...edPos, [l.numpcf]: e.target.value.replace(/\D/g, "") })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void gravarPosicao(l);
                      }
                    }}
                    onBlur={() => void gravarPosicao(l)}
                  />
                </td>
                <td className="pcf-row-acoes" onClick={(e) => e.stopPropagation()}>
                  {!modoSelecao && (
                    <>
                      <button className="btn-mini" title="Subir" disabled={i === 0} onClick={() => void mover(l.numpcf, "subir")}>Subir</button>
                      <button
                        className="btn-mini"
                        title="Descer"
                        disabled={i === linhas.length - 1}
                        onClick={() => void mover(l.numpcf, "descer")}
                      >Descer</button>
                    </>
                  )}
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
      {pedirMotivo && (
        <ModalMotivo
          titulo="Finalização Manual de PCF"
          pergunta="Informe o motivo da finalização."
          exigeMotivo
          onConfirmar={(m) => void finalizar(m)}
          onCancelar={() => setPedirMotivo(false)}
        />
      )}
    </div>
  );
}

// Busca de uma PCF por número (finalização manual / reversão) — LostFocus/Enter consulta.
export function CampoPcf({ onConsultar }: { onConsultar: (n: number) => void }) {
  const [v, setV] = useState("");
  return (
    <label className="pcf-campo pcf-sm">
      <span>PCF</span>
      <input
        className="num"
        inputMode="numeric"
        autoFocus
        value={v}
        onChange={(e) => setV(e.target.value.replace(/\D/g, ""))}
        onBlur={() => Number(v) && onConsultar(Number(v))}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (Number(v)) onConsultar(Number(v));
          }
        }}
      />
    </label>
  );
}

export function DadosPcf({ p, semSituacao }: { p: Pick<PcfResumo, "nomcli" | "tipo" | "numped" | "situac">; semSituacao?: boolean }): ReactNode {
  return (
    <div className="pcf-grid">
      <label className="pcf-campo pcf-lg">
        <span>Cliente</span>
        <input value={p.nomcli} readOnly tabIndex={-1} />
      </label>
      <label className="pcf-campo pcf-sm">
        <span>Tipo</span>
        <input value={p.tipo} readOnly tabIndex={-1} />
      </label>
      <label className="pcf-campo pcf-sm">
        <span>Pedido</span>
        <input className="num" value={p.numped || ""} readOnly tabIndex={-1} />
      </label>
      {!semSituacao && (
        <label className="pcf-campo pcf-sm">
          <span>Situação</span>
          <input value={p.situac} readOnly tabIndex={-1} />
        </label>
      )}
    </div>
  );
}

// ================================================================ finaliza_pcf_manual
export function FinalizacaoManual({ usuario, onVoltar }: { usuario: Usuario; onVoltar: () => void }) {
  const [p, setP] = useState<PcfResumo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [motivo, setMotivo] = useState(false);
  const [chave, setChave] = useState(0); // remonta o campo no Limpar
  const ordEt = useOrdenacao(p?.etapas, { pos: (e) => e.posicao, eta: (e) => e.descri });

  async function consultar(n: number) {
    setOk(null);
    const r = await apiProducao.manualConsultar(n);
    if (r.erro) {
      setErro(r.erro);
      limpar(false);
      return;
    }
    setErro(null);
    setP(r);
  }
  function limpar(tudo = true) {
    setP(null);
    if (tudo) setErro(null);
    setChave((k) => k + 1);
  }
  async function finalizar(m: string) {
    if (!p) return;
    setMotivo(false);
    if (!(await confirmar("Confirma a finalização desta PCF?"))) return;
    const r = await apiProducao.manualFinalizar(p.numpcf, m);
    if (!r.ok) return setErro(r.mensagem);
    setOk(`PCF ${p.numpcf} finalizada.`);
    limpar(false);
  }

  return (
    <div className="app">
      <Cabecalho usuario={usuario} titulo="Finalização Manual de PCF" sub="PCF" onVoltar={onVoltar} />
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <MsgModal texto={ok} tipo="ok" onClose={() => setOk(null)} />
      <div className="pcf-form">
        <section className="pcf-sec">
          <div className="pcf-grid">
            <CampoPcf key={chave} onConsultar={(n) => void consultar(n)} />
          </div>
          {p && <DadosPcf p={p} />}
        </section>
        {p && (
          <section className="pcf-sec">
            <div className="tabela-wrap">
              <table className="tabela-pcfs">
                <thead>
                  <tr>
                    <ThOrdena ord={ordEt} col="pos" className="num">Posição</ThOrdena>
                    <ThOrdena ord={ordEt} col="eta">Etapa</ThOrdena>
                    <th>Início</th>
                    <th>Término</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {/* MONTAR-TABELA (TELA-1.prc L335-336) só preenche Posição e Etapa: Início, Término e
                      Situação ficam em branco no legado — mantido igual (sem inventar fonte). */}
                  {ordEt.linhas.map((e) => (
                    <tr key={e.posicao}>
                      <td className="num">{e.posicao}</td>
                      <td>{e.descri}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
        <div className="pcf-acoes">
          <button className="btn-acao plan-del" disabled={!p} onClick={() => setMotivo(true)}>
            Finalizar
          </button>
          <button className="btn-secundario" onClick={() => limpar()}>
            Limpar
          </button>
          <span className="spacer" />
          <button className="btn-secundario btn-voltar-painel" onClick={onVoltar}>
            Sair
          </button>
        </div>
      </div>
      {motivo && p && (
        <ModalMotivo
          titulo="Finalização Manual de PCF"
          pergunta="O motivo é opcional."
          exigeMotivo={false}
          onConfirmar={(m) => void finalizar(m)}
          onCancelar={() => setMotivo(false)}
        />
      )}
    </div>
  );
}

// ================================================================ reverte_cancelamento_pcf
export function ReverterCancelamento({ usuario, onVoltar }: { usuario: Usuario; onVoltar: () => void }) {
  const [p, setP] = useState<PcfResumo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [chave, setChave] = useState(0);

  async function consultar(n: number) {
    setOk(null);
    const r = await apiProducao.reverterConsultar(n);
    if (r.erro) {
      setErro(r.erro);
      setP(null);
      return;
    }
    setErro(null);
    setP(r);
  }
  async function reverter() {
    if (!p) return;
    if (!(await confirmar("Confirma a reversão do cancelamento desta PCF?"))) return;
    const r = await apiProducao.reverter(p.numpcf);
    if (!r.ok) return setErro(r.mensagem);
    setOk(`Cancelamento da PCF ${p.numpcf} revertido — PCF aberta novamente.`);
    setP(null);
    setChave((k) => k + 1);
  }

  return (
    <div className="app">
      <Cabecalho usuario={usuario} titulo="Reverter Cancelamento de PCF" sub="PCF" onVoltar={onVoltar} />
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <MsgModal texto={ok} tipo="ok" onClose={() => setOk(null)} />
      <div className="pcf-form">
        <section className="pcf-sec">
          <div className="pcf-grid">
            <CampoPcf key={chave} onConsultar={(n) => void consultar(n)} />
          </div>
          {p && <DadosPcf p={p} semSituacao />}
        </section>
        <div className="pcf-acoes">
          <button className="btn-acao" disabled={!p} onClick={() => void reverter()}>
            Confirmar
          </button>
          <button
            className="btn-secundario"
            onClick={() => {
              setP(null);
              setErro(null);
              setChave((k) => k + 1);
            }}
          >
            Limpar
          </button>
        </div>
      </div>
    </div>
  );
}

// ================================================================ callpedido (card "Pedidos não confirmados")
// Opened chamado pelo painel (L707-711): cliente do painel, situação ABERTO travada, data 01/07/2023.
export function PedidosNaoConfirmados({ usuario, nomcli, onVoltar }: { usuario: Usuario; nomcli: string; onVoltar: () => void }) {
  const [empresas, setEmpresas] = useState<string[]>([]);
  const [nomemp, setNomemp] = useState("TODAS AS EMPRESAS");
  const [cliente, setCliente] = useState(nomcli === "TODOS" ? "" : nomcli);
  const [pedcli, setPedcli] = useState("");
  const [tipoData, setTipoData] = useState("EMISSÃO");
  const [datini, setDatini] = useState("2023-07-01");
  const [linhas, setLinhas] = useState<PedidoLinha[]>([]);
  const [total, setTotal] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const [erpUrl, setErpUrl] = useState("");
  const ord = useOrdenacao(linhas, {
    emp: (l) => l.nomemp, ped: (l) => l.numped, cli: (l) => l.nomcli, emi: (l) => l.datemi,
    ent: (l) => l.datent, tot: (l) => l.valtot, sit: (l) => l.situac,
  });

  async function ler() {
    try {
      setErro(null);
      const r = await apiPedidos.listar({ nomemp, nomcli: cliente, pedcli, situac: "ABERTO", tipoData, datini });
      setLinhas(r.linhas);
      setTotal(r.total);
    } catch (e) {
      setErro((e as Error).message);
    }
  }
  useEffect(() => {
    apiPedidos.empresas().then(setEmpresas).catch(() => setEmpresas([]));
    apiPcf.periodo().then((p) => setErpUrl(p.erpUrl ?? "")).catch(() => setErpUrl(""));
    void ler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="app">
      <Cabecalho usuario={usuario} titulo="Pedidos não confirmados" sub="Pedidos em aberto" onVoltar={onVoltar} />
      <div className="plan-filtros">
        <label className="plan-campo">
          <span>Empresa</span>
          <select value={nomemp} onChange={(e) => setNomemp(e.target.value)}>
            <option>TODAS AS EMPRESAS</option>
            {empresas.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label className="plan-campo">
          <span>Cliente</span>
          <input value={cliente} onChange={(e) => setCliente(e.target.value)} />
        </label>
        <label className="plan-campo">
          <span>Pedido do cliente</span>
          <input value={pedcli} onChange={(e) => setPedcli(e.target.value)} />
        </label>
        <label className="plan-campo">
          <span>Situação</span>
          <select value="ABERTO" disabled>
            <option>ABERTO</option>
          </select>
        </label>
        <label className="plan-campo">
          <span>Data de</span>
          <select value={tipoData} onChange={(e) => setTipoData(e.target.value)}>
            <option>EMISSÃO</option>
            <option>ENTREGA</option>
          </select>
        </label>
        <label className="plan-campo">
          <span>A partir de</span>
          <input type="date" value={datini} onChange={(e) => setDatini(e.target.value)} />
        </label>
        <button className="btn-acao" onClick={() => void ler()}>
          🔍 Pesquisar
        </button>
      </div>
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <div className="tabela-wrap">
        <table className="tabela-pcfs">
          <thead>
            <tr>
              <ThOrdena ord={ord} col="emp">Empresa</ThOrdena>
              <ThOrdena ord={ord} col="ped" className="num">Pedido</ThOrdena>
              <ThOrdena ord={ord} col="cli">Cliente</ThOrdena>
              <ThOrdena ord={ord} col="emi">Emissão</ThOrdena>
              <ThOrdena ord={ord} col="ent">Entrega</ThOrdena>
              <ThOrdena ord={ord} col="tot" className="num">Total</ThOrdena>
              <ThOrdena ord={ord} col="sit">Situação</ThOrdena>
            </tr>
          </thead>
          <tbody>
            {linhas.length === 0 && (
              <tr>
                <td colSpan={7} className="plan-vazio">
                  Nenhum pedido.
                </td>
              </tr>
            )}
            {ord.linhas.map((l) => (
              <tr key={l.numped}>
                <td>{l.nomemp}</td>
                <td className="num">
                  {erpUrl ? (
                    <a href={`${erpUrl}/?go=ATCADPED&numped=${l.numped}`} target="_blank" rel="noopener">
                      {l.numped}
                    </a>
                  ) : (
                    l.numped
                  )}
                </td>
                <td>{l.nomcli}</td>
                <td>{l.datemi}</td>
                <td>{l.datent}</td>
                <td className="num">{fmt2(l.valtot)}</td>
                <td>{l.situac}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}>
                <b>Total geral</b>
              </td>
              <td className="num">
                <b>{fmt2(total)}</b>
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
